"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import "react-datepicker/dist/react-datepicker.css";

import Loading from "../../Loading";
import { BackendURLContantas, URLConstants } from "@/constants/URLConstants";
import { useContestDelete } from "@/hooks/contest/useContestDelete";
import { useContestBulkActionByIds } from "@/hooks/contest/useContestBulkActionByIds";
import {
  BulkConfirmAction,
  ContestResponse,
  ContestResponseData,
  LockFilter,
} from "@/restapi/contest";
import { useTranslation } from "@/lib/react/i18n";

import { useContestCategoryInfenityScroll } from "@/hooks/contest/categoy/useContestCategoryInfenityScroll";
import StatusPills from "./StatusPills";
import InputSearcBox from "@/components/group/common/InputSearchBox";
import YesAndNoModal from "@/components/group/modals/YesAndNoModal";
import axios, { AxiosResponse } from "axios";
import {
  ContestCategoryIdData,
  ContestCategoryIdResponseData,
} from "@/restapi/contest/category";

import { useContestUsersInfinite } from "@/hooks/contest/useContestUsersInfinite";
import { toast } from "sonner";
import { SortBy, SortOrder } from "./ContestSortSection";

import type { AdminScope } from "@/components/contest/list/AdminScopePills";
import ContestRow from "./ContestRow";
import ContestBulkActionBar from "./ContestBulkActionBar";
import ContestFilterSection from "./ContestFilterSection";
import { User } from "next-auth";
import { useContestUpdate } from "@/hooks/contest/useContestUpdate";
import ContestLockModal from "./ContestLockModal";
import { UserRoleConst } from "@/restapi/user";

export const STATUS = {
  OPEN: "before_entry",
  SUBMISSION: "entry",
  SCORING: "contest",
  FINISHED: "closed",
} as const;

export type StatusKey = keyof typeof STATUS;

type Category = { id: number; name: string };
type UserMeta = { id: string; name: string };

export type ContestListMode = "published" | "settings";

export type ContestListProps = {
  mode?: ContestListMode;
  items: ContestResponseData[];
  loadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  user?: User & {
    name?: string | null;
    email?: string | null;
    image?: string | null;
  };
  isAdmin?: boolean;
  q: string;
  onChangeQ: (v: string) => void;
  onCommitSearch: () => void;
  selectedStatuses: StatusKey[];
  onChangeStatuses: (s: StatusKey[]) => void;
  selectedCategoryIds: number[];
  onChangeSelectedCategoryIds: (ids: number[]) => void;
  startFrom: string | null;
  onChangeStartFrom: (v: string | null) => void;
  startTo: string | null;
  onChangeStartTo: (v: string | null) => void;
  adminScope: AdminScope;
  onChangeAdminScope: (v: AdminScope) => void;
  selectedUserIds: string[];
  onChangeSelectedUserIds: (ids: string[]) => void;
  mutate: () => Promise<ContestResponse[] | undefined>;
  sortBy: SortBy;
  sortOrder: SortOrder;
  lockFilter: LockFilter;
  defaultLockFilter: LockFilter;
  onChangeSortBy: (v: SortBy) => void;
  onChangeSortOrder: (v: SortOrder) => void;
  onChangeLockFilter: (v: LockFilter) => void;
  shouldResetScroll?: boolean;
};

const formatRangeLabel = (startFrom: string | null, startTo: string | null) => {
  if (!startFrom && !startTo) return null;
  return `${startFrom ?? "…"} – ${startTo ?? "…"}`;
};

const RemovableChip = ({ label, onRemove }: { label: string; onRemove: () => void }) => {
  return (
    <span className="inline-flex items-center gap-2 pl-2 pr-1 py-1 rounded-full bg-white/10 border border-white/15 text-white/85">
      <span className="truncate max-w-[220px]">{label}</span>
      <button
        type="button"
        aria-label="remove filter"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/10 hover:bg-white/20 text-white/80"
      >
        ×
      </button>
    </span>
  );
};

async function fetchSelectedCategoriesByIds(
  ids: number[]
): Promise<ContestCategoryIdData[]> {
  if (ids.length === 0) return [];
  const qs = ids.map((id) => `ids[]=${encodeURIComponent(String(id))}`).join("&");
  const url = `${BackendURLContantas.CONTEST.CATEGORY.FIND.PATH_NAME}?${qs}`;
  const { data } = await axios.get<unknown, AxiosResponse<ContestCategoryIdResponseData>>(url);
  if (!data) throw new Error("Failed to fetch categories by ids");
  return data.data;
}

const ContestList = ({
  mode = "published",
  items,
  hasMore,
  loadMore,
  isLoading,
  user,
  isAdmin = false,
  q,
  onChangeQ,
  onCommitSearch,
  selectedStatuses,
  onChangeStatuses,
  selectedCategoryIds,
  onChangeSelectedCategoryIds,
  startFrom,
  onChangeStartFrom,
  startTo,
  onChangeStartTo,
  adminScope,
  onChangeAdminScope,
  selectedUserIds,
  onChangeSelectedUserIds,
  mutate,
  sortBy,
  sortOrder,
  onChangeSortBy,
  onChangeSortOrder,
  onChangeLockFilter,
  lockFilter,
  defaultLockFilter = "all",
  shouldResetScroll = false,
}: ContestListProps) => {
  const { submitDelete } = useContestDelete();
  const { submit: submitBulkActionByIds } = useContestBulkActionByIds();
  const { t, locale } = useTranslation();
  const { submit: submitUpdate } = useContestUpdate();

  const [publishSavingIds, setPublishSavingIds] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);

  const isPublishedMode = mode === "published";
  const isSettingsMode = mode === "settings";

  const contestUrl = isSettingsMode
    ? URLConstants.contest.edit.pathname
    : URLConstants.contest.group.pathname;

  const canShowDeleteUI = isSettingsMode;
  const canShowScopeUI = isSettingsMode;
  const canUseUserSelect = isPublishedMode || (canShowScopeUI && adminScope === "ALL");

  const [bulkMode, setBulkMode] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [bulkConfirmAction, setBulkConfirmAction] = useState<BulkConfirmAction>(null);
  const [lockModalOpen, setLockModalOpen] = useState(false);

  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!bulkMode) {
      setCheckedIds(new Set());
      setBulkConfirmAction(null);
      setLockModalOpen(false);
    }
  }, [bulkMode]);

  useEffect(() => {
    if (shouldResetScroll && scrollAreaRef.current) {
      scrollAreaRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [shouldResetScroll]);

  const toggleCheck = useCallback((contestId: number) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(contestId)) next.delete(contestId);
      else next.add(contestId);
      return next;
    });
  }, []);

  const allIdsOnPage = useMemo(() => items.map((c) => c.id), [items]);

  const allCheckedOnPage = useMemo(() => {
    if (allIdsOnPage.length === 0) return false;
    return allIdsOnPage.every((id) => checkedIds.has(id));
  }, [allIdsOnPage, checkedIds]);

  const selectedCount = checkedIds.size;
  const selectedIds = useMemo(() => Array.from(checkedIds), [checkedIds]);

  const toggleSelectAllOnPage = useCallback(() => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (allIdsOnPage.length === 0) return next;

      const every = allIdsOnPage.every((id) => next.has(id));
      if (every) {
        allIdsOnPage.forEach((id) => next.delete(id));
      } else {
        allIdsOnPage.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allIdsOnPage]);

  const bulkActionGuard = useCallback(() => {
    if (selectedCount === 0) {
      toast.info(t("Please select contests."));
      return false;
    }
    return true;
  }, [selectedCount, t]);

  const openBulkConfirm = useCallback(
    (action: BulkConfirmAction) => {
      if (!action) return;
      if (!bulkActionGuard()) return;
      setBulkConfirmAction(action);
    },
    [bulkActionGuard]
  );

  const closeBulkConfirm = useCallback(() => {
    if (sending) return;
    setBulkConfirmAction(null);
  }, [sending]);

  const executeBulkPublish = useCallback(async () => {
    if (!bulkActionGuard()) return;

    try {
      setSending(true);
      const res = await submitBulkActionByIds({
        contestIds: selectedIds,
        action: "publish",
        actorUuid: user?.id ?? '',
      });

      if (res) {
        toast.success(t("Contests published."));
        setBulkConfirmAction(null);
        setCheckedIds(new Set());
        await mutate();
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Failed to publish"));
    } finally {
      setSending(false);
    }
  }, [bulkActionGuard, mutate, selectedIds, submitBulkActionByIds, t]);

  const executeBulkUnpublish = useCallback(async () => {
    if (!bulkActionGuard()) return;

    try {
      setSending(true);
      const res = await submitBulkActionByIds({
        contestIds: selectedIds,
        action: "unpublish",
        actorUuid: user?.id ?? '',
      });

      if (res) {
        toast.success(t("Contests unpublished."));
        setBulkConfirmAction(null);
        setCheckedIds(new Set());
        await mutate();
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Failed to unpublish"));
    } finally {
      setSending(false);
    }
  }, [bulkActionGuard, mutate, selectedIds, submitBulkActionByIds, t,]);

  const executeBulkDelete = useCallback(async () => {
    if (!bulkActionGuard()) return;

    try {
      setSending(true);
      const res = await submitBulkActionByIds({
        contestIds: selectedIds,
        action: "delete",
        actorUuid: user?.id ?? '',
      });

      if (res) {
        toast.success(t("Contests deleted"));
        setBulkConfirmAction(null);
        setCheckedIds(new Set());
        await mutate();
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Failed to delete"));
    } finally {
      setSending(false);
    }
  }, [bulkActionGuard, mutate, selectedIds, submitBulkActionByIds, t]);

  const executeBulkUnlock = useCallback(async () => {
    if (!bulkActionGuard()) return;

    try {
      setSending(true);
      const res = await submitBulkActionByIds({
        contestIds: selectedIds,
        action: "unlock",
        actorUuid: user?.id ?? '',
      });

      if (res) {
        toast.success(t("Contests unlocked"));
        setCheckedIds(new Set());
        await mutate();
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Failed to unlock"));
    } finally {
      setSending(false);
    }
  }, [bulkActionGuard, mutate, selectedIds, submitBulkActionByIds, t]);

  const confirmBulkAction = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (bulkConfirmAction === "publish") {
        await executeBulkPublish();
        return;
      }

      if (bulkConfirmAction === "unpublish") {
        await executeBulkUnpublish();
        return;
      }

      if (bulkConfirmAction === "delete") {
        await executeBulkDelete();
      }
    },
    [bulkConfirmAction, executeBulkDelete, executeBulkPublish, executeBulkUnpublish]
  );

  const onBulkPublish = useCallback(() => openBulkConfirm("publish"), [openBulkConfirm]);
  const onBulkUnpublish = useCallback(() => openBulkConfirm("unpublish"), [openBulkConfirm]);
  const onBulkDelete = useCallback(() => openBulkConfirm("delete"), [openBulkConfirm]);

  const onBulkLock = useCallback(() => {
    if (!bulkActionGuard()) return;
    setLockModalOpen(true);
  }, [bulkActionGuard]);

  const onBulkUnlock = useCallback(async () => {
    await executeBulkUnlock();
  }, [executeBulkUnlock]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const userSheetEnabled = isFilterOpen && canUseUserSelect;

  const userLabelKey = "Contest organizer";
  const [userSearchValue, setUserSearchValue] = useState("");

  const userMetaMapRef = useRef<Map<string, UserMeta>>(new Map());
  const [userMetaVersion, setUserMetaVersion] = useState(0);

  const {
    items: users,
    isReachingEnd,
    loadMore: userLoadMore,
    reset: resetUserPages,
    error: usersError,
    isLoadingInitial: usersLoadingInitial,
  } = useContestUsersInfinite({
    enabled: userSheetEnabled,
    q: userSearchValue,
    perPage: 20,
  });

  const userHasMore = !isReachingEnd;

  useEffect(() => {
    if (!users || users.length === 0) return;

    let changed = false;

    for (const u of users as any[]) {
      const id = String(u.uuid ?? u.id);
      const name = String(u.name ?? "");
      if (id && !userMetaMapRef.current.has(id)) {
        userMetaMapRef.current.set(id, { id, name });
        changed = true;
      }
    }

    if (changed) setUserMetaVersion((v) => v + 1);
  }, [users]);

  const selectedUsers = useMemo(() => {
    void userMetaVersion;
    return (selectedUserIds ?? [])
      .map((id) => userMetaMapRef.current.get(String(id)))
      .filter(Boolean) as UserMeta[];
  }, [selectedUserIds, userMetaVersion]);

  const handleUserSearchCommit = useCallback(
    (keyword: string) => {
      setUserSearchValue(keyword);
      resetUserPages();
      const el = document.getElementById("userScrollArea");
      if (el) el.scrollTo({ top: 0, behavior: "auto" });
    },
    [resetUserPages]
  );

  const selectedContests = useMemo(() => {
    return items
      .filter((contest) => checkedIds.has(contest.id))
      .map((contest) => ({
        id: contest.id,
        title: contest.title,
        isLocked: contest.isLocked,
        lockReason: contest.lockReason,
      }));
  }, [items, checkedIds]);

  const [categorySearchValue, setCategorySearchValue] = useState("");

  const {
    items: categories,
    isError: categoriesError,
    hasMore: categoryHasMore,
    loadMore: categoryLoadMore,
    reset: resetCategoryPages,
  } = useContestCategoryInfenityScroll({
    isActive: true,
    query: categorySearchValue,
  });

  const handleCategorySearchCommit = useCallback(
    (keyword: string) => {
      setCategorySearchValue(keyword);
      resetCategoryPages();
      const el = document.getElementById("categoryScrollArea");
      if (el) el.scrollTo({ top: 0, behavior: "auto" });
    },
    [resetCategoryPages]
  );

  const [selectedCategoryMeta, setSelectedCategoryMeta] = useState<Category[]>([]);
  const [selectedCategoryMetaLoading, setSelectedCategoryMetaLoading] = useState(false);

  useEffect(() => {
    if (!categories || categories.length === 0) return;

    setSelectedCategoryMeta((prev) => {
      const map = new Map<number, Category>(prev.map((c) => [c.id, c]));
      let changed = false;

      for (const c of categories as any[]) {
        const id = Number(c.id);
        if (!map.has(id)) {
          map.set(id, { id, name: String(c.name) });
          changed = true;
        }
      }

      return changed ? Array.from(map.values()) : prev;
    });
  }, [categories]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const ids = (selectedCategoryIds ?? [])
        .map((n) => Number(n))
        .filter((n) => !Number.isNaN(n));

      if (ids.length === 0) {
        setSelectedCategoryMeta([]);
        setSelectedCategoryMetaLoading(false);
        return;
      }

      try {
        setSelectedCategoryMetaLoading(true);

        const fetched = await fetchSelectedCategoriesByIds(ids);
        if (cancelled) return;

        const normalized: Category[] = fetched.map((c) => ({
          id: Number(c.id),
          name: String(c.name),
        }));

        setSelectedCategoryMeta(normalized);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) {
          setSelectedCategoryMetaLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [selectedCategoryIds]);

  const selectedCategories = useMemo(() => {
    return selectedCategoryIds
      .map((id) => selectedCategoryMeta.find((c) => c.id === id))
      .filter(Boolean) as Category[];
  }, [selectedCategoryIds, selectedCategoryMeta]);

  const hasAnyFilter =
    selectedCategoryIds.length > 0 ||
    !!startFrom ||
    !!startTo ||
    lockFilter !== "all" ||
    (canUseUserSelect && selectedUserIds.length > 0);

  const filterSubtitle = useMemo(() => {
    if (!hasAnyFilter && !canShowScopeUI) return t("Select category and period");
    if (!hasAnyFilter && canShowScopeUI) {
      return `${t("Scope")}: ${adminScope === "MINE" ? t("Mine") : t("All")}`;
    }

    const parts: string[] = [];

    if (canShowScopeUI) {
      parts.push(`${t("Scope")}: ${adminScope === "MINE" ? t("Mine") : t("All")}`);
      if (adminScope === "ALL") {
        parts.push(
          selectedUserIds.length > 0
            ? `${t("User")}: ${selectedUserIds.length}`
            : `${t("User")}: ${t("All")}`
        );
      }
    }

    if (isPublishedMode) {
      parts.push(
        selectedUserIds.length > 0
          ? `${t(userLabelKey)}: ${selectedUserIds.length}`
          : `${t(userLabelKey)}: ${t("All")}`
      );
    }

    if (selectedCategoryIds.length > 0) {
      const firstName = selectedCategories[0]?.name;
      const rest = selectedCategoryIds.length - 1;
      parts.push(
        firstName
          ? rest > 0
            ? `${t("Category")}: ${firstName} +${rest}`
            : `${t("Category")}: ${firstName}`
          : selectedCategoryMetaLoading
            ? `${t("Category")}: Loading...`
            : `${t("Category")}: ${selectedCategoryIds.length}`
      );
    }

    if (lockFilter === "locked") {
      parts.push(`${t("Lock")}: ${t("Locked")}`);
    }
    if (lockFilter === "unlocked") {
      parts.push(`${t("Lock")}: ${t("Unlocked")}`);
    }

    const range = formatRangeLabel(startFrom, startTo);
    if (range) parts.push(`${t("Period")}: ${range}`);

    return parts.join(" / ");
  }, [
    adminScope,
    canShowScopeUI,
    hasAnyFilter,
    isPublishedMode,
    selectedUserIds.length,
    selectedCategoryIds.length,
    selectedCategories,
    selectedCategoryMetaLoading,
    startFrom,
    startTo,
    t,
    lockFilter,
  ]);

  const clearFilters = () => {
    onChangeSelectedCategoryIds([]);
    onChangeStartFrom(null);
    onChangeStartTo(null);
    onChangeLockFilter(defaultLockFilter);
    if (canUseUserSelect) onChangeSelectedUserIds([]);
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; title?: string } | null>(null);

  const openDeleteModal = useCallback((contest: ContestResponseData) => {
    setDeleteTarget({ id: contest.id, title: contest.title ?? undefined });
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (sending) return;
    setIsDeleteModalOpen(false);
    setDeleteTarget(null);
  }, [sending]);

  const handleTogglePublish = useCallback(
    async (contest: ContestResponseData, next: boolean) => {
      if (publishSavingIds.has(contest.id)) return;

      try {
        setPublishSavingIds((prev) => new Set(prev).add(contest.id));

        await submitUpdate({
          id: contest.id,
          title: contest.title ?? contest.group.name ?? "",
          description: contest.description ?? "",
          startDate: contest.startDate ?? null,
          scoringStartDate: contest.scoringStartDate ?? null,
          contestEndDate: contest.contestEndDate ?? null,
          isPublished: next,
          groupId: contest.group.id,
        });

        toast.success(next ? t("Contest published") : t("Contest unpublished"));
        await mutate();
      } catch (error) {
        console.error(error);
        toast.error(t("Failed to update"));
      } finally {
        setPublishSavingIds((prev) => {
          const nextSet = new Set(prev);
          nextSet.delete(contest.id);
          return nextSet;
        });
      }
    },
    [mutate, publishSavingIds, submitUpdate, t]
  );

  const confirmDelete = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!deleteTarget) return;

      try {
        setSending(true);
        const res = await submitDelete({ id: deleteTarget.id });

        if (res) {
          setIsDeleteModalOpen(false);
          setDeleteTarget(null);
          toast.success(t("Contest deleted."));
          await mutate();
        }
      } catch (error) {
        console.error(error);
        toast.error(t("Failed to delete"));
      } finally {
        setSending(false);
      }
    },
    [deleteTarget, mutate, submitDelete, t]
  );

  const userSheetItems = useMemo(() => {
    return (users as any[]).map((u) => ({
      id: String(u.uuid ?? u.id),
      name: String(u.name ?? ""),
    }));
  }, [users]);

  const categorySheetItems = useMemo(() => {
    return (categories as any[]).map((c) => ({
      id: Number(c.id),
      name: c.name,
    }));
  }, [categories]);

  if (categoriesError) return <></>;
  if (usersError) return <></>;

  const deleteMessages = [
    t("Your contest will be deleted."),
    t("The space will remain."),
    t("This action cannot be undone."),
    deleteTarget?.title ? `${t("Contest")}: ${deleteTarget.title}` : "",
  ].filter(Boolean);

  const bulkConfirmMessages = (() => {
    if (bulkConfirmAction === "publish") {
      return [
        t("The selected contests will be published."),
        `${t("Selected")}: ${selectedCount}`,
        t("Please confirm this action."),
      ];
    }

    if (bulkConfirmAction === "unpublish") {
      return [
        t("The selected contests will be unpublished."),
        `${t("Selected")}: ${selectedCount}`,
        t("Please confirm this action."),
      ];
    }

    if (bulkConfirmAction === "delete") {
      return [
        t("The selected contests will be deleted."),
        `${t("Selected")}: ${selectedCount}`,
        t("The space will remain."),
        t("This action cannot be undone."),
      ];
    }

    return [];
  })();

  return (
    <div className="mx-auto max-w-4xl h-dvh bg-black text-white flex flex-col overflow-hidden">
      <YesAndNoModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        alertMessages={deleteMessages}
      />

      <YesAndNoModal
        isOpen={bulkConfirmAction !== null}
        onClose={closeBulkConfirm}
        onConfirm={confirmBulkAction}
        alertMessages={bulkConfirmMessages}
      />

      {user && user.role === UserRoleConst.ADMIN && (
        <ContestLockModal
          isOpen={lockModalOpen}
          contestIds={selectedIds}
          actorUuid={user.id}
          targets={selectedContests}
          onClose={() => setLockModalOpen(false)}
          onSuccess={async () => {
            setLockModalOpen(false);
            setCheckedIds(new Set());
            await mutate();
          }}
        />


      )}
      <div className="shrink-0 px-2 pt-6 border-white/15">
        <div className="my-6 flex justify-center">
          <InputSearcBox
            inputTestId="contest-search"
            value={q}
            onChange={(e) => onChangeQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            onBlur={() => onCommitSearch()}
            placeholder={t("Search by contest name")}
            className="bg-black"
          />
        </div>

        <div className="mb-0 flex justify-center">
          <StatusPills selected={selectedStatuses} onChange={onChangeStatuses} t={t} />
        </div>

        {canShowDeleteUI && (
          <ContestBulkActionBar
            bulkMode={bulkMode}
            selectedCount={selectedCount}
            allCheckedOnPage={allCheckedOnPage}
            onEnableBulkMode={() => setBulkMode(true)}
            onDisableBulkMode={() => setBulkMode(false)}
            onToggleSelectAllOnPage={toggleSelectAllOnPage}
            onBulkPublish={onBulkPublish}
            onBulkUnpublish={onBulkUnpublish}
            onBulkDelete={onBulkDelete}
            onBulkLock={onBulkLock}
            onBulkUnlock={onBulkUnlock}
            canUseLockActions={!!isAdmin}
            t={t}
          />
        )}

        <ContestFilterSection
          lockFilter={lockFilter}
          onChangeLockFilter={onChangeLockFilter}
          open={isFilterOpen}
          onToggle={() => setIsFilterOpen((v) => !v)}
          title={t("Filter")}
          subtitle={filterSubtitle}
          canShowScopeUI={canShowScopeUI}
          adminScope={adminScope}
          onChangeAdminScope={onChangeAdminScope}
          t={t}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onChangeSortBy={onChangeSortBy}
          onChangeSortOrder={onChangeSortOrder}
          onCommitSearch={onCommitSearch}
          canUseUserSelect={canUseUserSelect}
          userLabelKey={userLabelKey}
          selectedUserIds={selectedUserIds}
          onChangeSelectedUserIds={onChangeSelectedUserIds}
          userSheetItems={userSheetItems}
          userSearchValue={userSearchValue}
          handleUserSearchCommit={handleUserSearchCommit}
          userLoadMore={userLoadMore}
          userHasMore={userHasMore}
          usersLoadingInitial={usersLoadingInitial}
          selectedCategoryIds={selectedCategoryIds}
          onChangeSelectedCategoryIds={onChangeSelectedCategoryIds}
          categories={categorySheetItems}
          categorySearchValue={categorySearchValue}
          handleCategorySearchCommit={handleCategorySearchCommit}
          categoryLoadMore={categoryLoadMore}
          categoryHasMore={categoryHasMore}
          startFrom={startFrom}
          startTo={startTo}
          onChangeStartFrom={onChangeStartFrom}
          onChangeStartTo={onChangeStartTo}
          locale={locale}
          hasAnyFilter={hasAnyFilter}
          clearFilters={clearFilters}
        />

        {hasAnyFilter && (
          <div className="relative z-10 mt-4 mb-4 space-y-2 text-xs">
            {(selectedCategories.length > 0 ||
              startFrom ||
              startTo ||
              lockFilter !== "all" ||
              (canUseUserSelect && selectedUserIds.length > 0)) && (
                <>
                  <div className="flex items-center gap-3 my-2">
                    <div className="h-px flex-1 bg-white/10" />
                    <span className="text-[10px] text-white/50 whitespace-nowrap">
                      {t("Filter")}
                    </span>
                    <div className="h-px flex-1 bg-white/10" />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {canUseUserSelect &&
                      selectedUsers.map((u) => (
                        <RemovableChip
                          key={u.id}
                          label={u.name || u.id}
                          onRemove={() =>
                            onChangeSelectedUserIds(selectedUserIds.filter((id) => id !== u.id))
                          }
                        />
                      ))}

                    {selectedCategories.map((c) => (
                      <RemovableChip
                        key={c.id}
                        label={c.name}
                        onRemove={() =>
                          onChangeSelectedCategoryIds(
                            selectedCategoryIds.filter((id) => id !== c.id)
                          )
                        }
                      />
                    ))}

                    {(startFrom || startTo) && (
                      <RemovableChip
                        label={formatRangeLabel(startFrom, startTo) ?? ""}
                        onRemove={() => {
                          onChangeStartFrom(null);
                          onChangeStartTo(null);
                        }}
                      />
                    )}

                    {lockFilter !== "all" && isAdmin && isSettingsMode && (
                      <RemovableChip
                        label={lockFilter === "locked" ? t("Locked") : t("Unlocked")}
                        onRemove={() => onChangeLockFilter("all")}
                      />
                    )}
                  </div>
                </>
              )}
          </div>
        )}

        <div className="border-b border-white/15" />
      </div>

      <div ref={scrollAreaRef} id="contestScrollArea" className="flex-1 min-h-0 overflow-auto hide-scrollbar">
        <InfiniteScroll
          dataLength={items.length}
          next={loadMore}
          hasMore={hasMore}
          scrollableTarget="contestScrollArea"
          loader={
            <div className="py-6 flex justify-center">
              <Loading />
            </div>
          }
        >
          <ul className="px-4 pb-40">
            {isLoading && (
              <div className="py-6 flex justify-center">
                <Loading />
              </div>
            )}

            {items.length === 0 && !isLoading && (
              <li
                data-testid="empty-list-message"
                className="px-4 py-10 text-white/60 text-sm flex justify-center"
              >
                {t("No contests found")}
              </li>
            )}

            {items.map((contest) => {
              const href = isSettingsMode
                ? `${contestUrl}/${contest.id}`
                : `${contestUrl}/${contest.group.id}`;
              const disableLink =
                isSettingsMode &&
                !isAdmin &&
                !!contest.isLocked;
              return (
                <ContestRow
                  key={contest.id}
                  canShowDeleteUI={canShowDeleteUI}
                  href={href}
                  contest={contest}
                  t={t}
                  locale={locale}
                  sending={sending}
                  openDeleteModal={openDeleteModal}
                  bulkMode={bulkMode}
                  checked={checkedIds.has(contest.id)}
                  onToggleCheck={toggleCheck}
                  onTogglePublish={handleTogglePublish}
                  publishSaving={publishSavingIds.has(contest.id)}
                  disableLink={disableLink}
                  mode={mode}
                />
              );
            })}
          </ul>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export { ContestList };
